<template>
    <div class="manifest-component-arguments">
        <div
            v-for="argument in descriptors"
            :key="argument.name"
            class="manifest-component-arguments__field"
        >
            <div class="d-flex align-items-center justify-content-between mb-25">
                <label
                    :for="argumentId(argument)"
                    class="manifest-component-arguments__label mb-0"
                >
                    {{ argument.title || argument.name }}
                    <span v-if="argument.required" class="text-danger">*</span>
                </label>
                <b-button
                    v-if="hasValue(argument) && !disabled"
                    size="sm"
                    variant="flat-secondary"
                    class="manifest-component-arguments__clear p-0"
                    @click="clearArgument(argument)"
                >
                    Clear
                </b-button>
            </div>

            <small v-if="argument.description" class="d-block text-muted mb-50">
                {{ argument.description }}
            </small>

            <b-form-checkbox
                v-if="argument.nullable"
                :checked="isNull(argument)"
                :disabled="disabled"
                class="mb-50"
                @input="toggleNull(argument, $event)"
            >
                Use null
            </b-form-checkbox>

            <template v-if="!isNull(argument)">
                <b-form-select
                    v-if="hasAllowedValues(argument)"
                    :id="argumentId(argument)"
                    :value="argumentValue(argument)"
                    :options="allowedValueOptions(argument)"
                    :state="argumentState(argument)"
                    :disabled="disabled"
                    @input="updateArgument(argument, $event)"
                />

                <b-form-select
                    v-else-if="argument.type === 'boolean'"
                    :id="argumentId(argument)"
                    :value="argumentValue(argument)"
                    :options="booleanOptions(argument)"
                    :state="argumentState(argument)"
                    :disabled="disabled"
                    @input="updateArgument(argument, $event)"
                />

                <b-form-tags
                    v-else-if="argument.type === 'string_list'"
                    :id="argumentId(argument)"
                    :value="listValue(argument)"
                    :state="argumentState(argument)"
                    :disabled="disabled"
                    separator=",;"
                    placeholder="Add a value and press Enter"
                    remove-on-delete
                    @input="updateArgument(argument, $event)"
                />

                <b-form-textarea
                    v-else-if="isJsonArgument(argument)"
                    :id="argumentId(argument)"
                    :value="jsonDrafts[argument.name]"
                    :state="argumentState(argument)"
                    :disabled="disabled"
                    rows="4"
                    placeholder="{}"
                    @input="updateJsonDraft(argument, $event)"
                    @blur="commitJson(argument)"
                />

                <b-form-input
                    v-else
                    :id="argumentId(argument)"
                    :type="isNumericArgument(argument) ? 'number' : 'text'"
                    :step="numericStep(argument)"
                    :value="argumentValue(argument)"
                    :state="argumentState(argument)"
                    :disabled="disabled"
                    @input="updateScalar(argument, $event)"
                />
            </template>

            <b-form-invalid-feedback :state="argumentState(argument)">
                {{ argumentError(argument) }}
            </b-form-invalid-feedback>
        </div>
    </div>
</template>

<script>
import {
    BButton,
    BFormCheckbox,
    BFormInput,
    BFormInvalidFeedback,
    BFormSelect,
    BFormTags,
    BFormTextarea,
} from 'bootstrap-vue'
import {
    argumentDescriptors,
    validateArgument,
} from './manifestArgumentSchema'

export default {
    components: {
        BButton,
        BFormCheckbox,
        BFormInput,
        BFormInvalidFeedback,
        BFormSelect,
        BFormTags,
        BFormTextarea,
    },
    props: {
        value: {
            type: Object,
            default: () => ({}),
        },
        schema: {
            type: Object,
            default: () => ({}),
        },
        disabled: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            jsonDrafts: {},
            jsonErrors: {},
        }
    },
    computed: {
        descriptors() {
            return argumentDescriptors(this.schema)
        },
    },
    watch: {
        value: {
            deep: true,
            immediate: true,
            handler() {
                this.syncJsonDrafts()
            },
        },
        schema: {
            deep: true,
            handler() {
                this.syncJsonDrafts()
            },
        },
    },
    methods: {
        argumentId(argument) {
            return `manifest-component-argument-${this._uid}-${argument.name}`
        },
        hasValue(argument) {
            return Object.prototype.hasOwnProperty.call(this.value, argument.name)
        },
        argumentValue(argument) {
            return this.hasValue(argument) ? this.value[argument.name] : undefined
        },
        argumentError(argument) {
            return this.jsonErrors[argument.name]
                || validateArgument(argument, this.value)
        },
        argumentState(argument) {
            return this.argumentError(argument) ? false : null
        },
        hasAllowedValues(argument) {
            return Array.isArray(argument.allowedValues) && argument.allowedValues.length > 0
        },
        allowedValueOptions(argument) {
            return [
                {
                    value: undefined,
                    text: 'Select a value',
                    disabled: argument.required,
                },
                ...argument.allowedValues.map(value => ({
                    value,
                    text: String(value),
                })),
            ]
        },
        booleanOptions(argument) {
            return [
                {
                    value: undefined,
                    text: 'Select true or false',
                    disabled: argument.required,
                },
                { value: true, text: 'True' },
                { value: false, text: 'False' },
            ]
        },
        isNumericArgument(argument) {
            return argument.type === 'integer' || argument.type === 'number'
        },
        numericStep(argument) {
            if (argument.type === 'integer') {
                return '1'
            }
            return argument.type === 'number' ? 'any' : null
        },
        isJsonArgument(argument) {
            return argument.type === 'map' || argument.type === 'object'
        },
        isNull(argument) {
            return this.hasValue(argument) && this.argumentValue(argument) === null
        },
        listValue(argument) {
            const value = this.argumentValue(argument)
            return Array.isArray(value) ? value : []
        },
        updateArgument(argument, value) {
            if (this.disabled) {
                return
            }

            if (value === undefined) {
                this.clearArgument(argument)
                return
            }

            this.$emit('input', {
                ...this.value,
                [argument.name]: value,
            })
        },
        updateScalar(argument, value) {
            if (value === '') {
                this.clearArgument(argument)
                return
            }

            if (this.isNumericArgument(argument)) {
                this.updateArgument(argument, Number(value))
                return
            }

            this.updateArgument(argument, value)
        },
        clearArgument(argument) {
            if (this.disabled) {
                return
            }

            const values = { ...this.value }
            delete values[argument.name]
            this.$delete(this.jsonErrors, argument.name)
            if (this.isJsonArgument(argument)) {
                this.$set(this.jsonDrafts, argument.name, '')
            }
            this.$emit('input', values)
        },
        toggleNull(argument, useNull) {
            if (useNull) {
                this.updateArgument(argument, null)
            } else {
                this.clearArgument(argument)
            }
        },
        updateJsonDraft(argument, value) {
            this.$set(this.jsonDrafts, argument.name, value)
            this.$delete(this.jsonErrors, argument.name)
        },
        commitJson(argument) {
            if (this.disabled) {
                return
            }

            const draft = this.jsonDrafts[argument.name]
            if (!draft || !draft.trim()) {
                this.clearArgument(argument)
                return
            }

            try {
                const value = JSON.parse(draft)
                if (!value || typeof value !== 'object' || Array.isArray(value)) {
                    throw new Error('Expected an object')
                }
                this.$delete(this.jsonErrors, argument.name)
                this.updateArgument(argument, value)
            } catch (error) {
                this.$set(this.jsonErrors, argument.name, 'Enter a valid JSON object.')
            }
        },
        syncJsonDrafts() {
            this.descriptors
                .filter(argument => this.isJsonArgument(argument))
                .forEach(argument => {
                    const value = this.argumentValue(argument)
                    const draft = value && typeof value === 'object' && !Array.isArray(value)
                        ? JSON.stringify(value, null, 2)
                        : ''
                    this.$set(this.jsonDrafts, argument.name, draft)
                    this.$delete(this.jsonErrors, argument.name)
                })
        },
    },
}
</script>

<style lang="scss" scoped>
.manifest-component-arguments {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr));
    gap: 1rem;
}

.manifest-component-arguments__field {
    min-width: 0;
}

.manifest-component-arguments__label {
    color: var(--manifest-secondary-text-color, #45434c);
    font-size: .857rem;
    font-weight: 500;
}

.manifest-component-arguments__clear {
    min-height: auto;
    font-size: .75rem;
}

@media (max-width: 767.98px) {
    .manifest-component-arguments {
        grid-template-columns: 1fr;
    }
}
</style>
